import React, { forwardRef } from 'react';
import type {
  AutocompleteRenderGetTagProps,
  AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete';

import Input from '../../Input';
import BaseAutocomplete from '../BaseAutocomplete';
import { RenderInputParams } from '../BaseAutocomplete/types';
import SelectOption from '../components/SelectOption';
import SelectTag from '../components/SelectTag';
import { defaultOptionLabelRenderer, defaultTagLabelRenderer } from './renderers';
import { AutocompleteProps } from './types';

const Autocomplete = <
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = false,
>(
  props: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  ref: React.Ref<HTMLDivElement>,
) => {
  const {
    getOptionLabel,
    multiple,
    optionLabelRenderer,
    size = 'medium',
    tagLabelRenderer,
    ...rest
  } = props;

  const renderInput = (params: RenderInputParams) => <Input {...params} />;

  const renderOption = (option: T, state: AutocompleteRenderOptionState) => {
    let label: React.ReactNode;

    if (optionLabelRenderer) {
      label = optionLabelRenderer(option, state);
    } else {
      label = defaultOptionLabelRenderer(getOptionLabel(option), state, size);
    }

    return <SelectOption label={label} multiple={multiple} size={size} state={state} />;
  };

  const renderTags = (options: T[], getTagProps: AutocompleteRenderGetTagProps) =>
    options.map((option, index) => {
      let label: React.ReactNode;

      if (tagLabelRenderer) {
        label = tagLabelRenderer(option);
      } else {
        label = defaultTagLabelRenderer(getOptionLabel(option), size);
      }

      return <SelectTag label={label} size={size} {...getTagProps({ index })} />;
    });

  return (
    <BaseAutocomplete
      ref={ref}
      openOnFocus
      disableCloseOnSelect={multiple}
      getOptionLabel={getOptionLabel}
      multiple={multiple}
      renderInput={renderInput}
      renderOption={renderOption}
      renderTags={renderTags}
      size={size}
      {...rest}
    />
  );
};

export default forwardRef(Autocomplete) as typeof Autocomplete;
